import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { AccountUpdateRequest } from '@/api/handlers/resources/accounts/requests';
import type { AccountWriteResponse } from '@/api/handlers/resources/accounts/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { AccountQueryKeys } from './accountQueryKeys';

export type UpdateAccountVariables = {
    id: string;
    body: AccountUpdateRequest;
};

/**
 * Update an account's editable fields — patch-style, so the body carries only
 * what changed plus the `etag` the edit was made against.
 *
 * A `409 STALE_UPDATE` comes back as an ordinary `{ success: false }` envelope
 * with `error.code === 'STALE_UPDATE'`. It is **not** handled here on purpose:
 * recovering means re-reading the account, replaying the user's edits onto the
 * fresh state and resubmitting with the new etag, and only the form knows what
 * the user typed. A silent retry here would overwrite whatever the other editor
 * just saved.
 *
 * Invalidates the whole accounts key so a renumbered code, a rename or a
 * reparent is reflected everywhere the tree shows it — and so the cached detail
 * holding the now-spent etag is dropped.
 */
export const useUpdateAccountMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<AccountWriteResponse>,
        Error,
        UpdateAccountVariables
    >({
        mutationFn: ({ id, body }) =>
            apiClient.accounts.updateAccount(id, body),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: AccountQueryKeys.all,
                });
            }
        },
    });
};
